// Root Route - Premium Midnight Luxe Design with TAO Solutions Branding
import { createRootRouteWithContext, Outlet, Link } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { AuthContextValue, useAuth } from '@/providers/AuthProvider';
import { Toaster } from '@/components/ui/toaster';
import { TaoLogo, PoweredByTao } from '@/components/ui/tao-logo';
import {
  LayoutDashboard,
  FileText,
  FolderOpen,
  Settings,
  LogOut,
  Menu,
  Workflow,
  ChevronLeft,
  KeyRound,
  DollarSign,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import api from '@/lib/api-client';
import { CreditNovaLogoNoLetters } from '@/components/ui/credit-nova-logo-no-letters';
import { OnboardingGate } from '@/components/onboarding/OnboardingGate';

interface RouterContext {
  queryClient: QueryClient;
  auth: AuthContextValue;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  const { isAuthenticated, isLoading: authLoading, user: authUser, logout, login } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile', authUser?.localAccountId],
    queryFn: async () => {
      const response = await api.users.me();
      return (response && typeof response === 'object' && 'data' in response) 
        ? (response as any).data 
        : response;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  // Combine auth user and DB profile into one object
  // This ensures 'user' exists for all child components
  const user = profile ? { ...authUser, ...profile } : null;

  const isLoading = authLoading || (isAuthenticated && profileLoading);
  const isAdmin = profile?.role === 'admin';
  const preferredMode = profile?.preferredMode as 'direct' | 'api' | null | undefined;

  // Fetch pending join request count for admin badge
  const { data: pendingJoinRequestCount } = useQuery<number>({
    queryKey: ['admin', 'join-requests', 'pending-count'],
    queryFn: async () => {
      const result = await api.onboarding.listJoinRequests({ status: 'pending' });
      return Array.isArray(result) ? result.length : 0;
    },
    enabled: isAdmin && isAuthenticated && !!profile?.clientId,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    refetchInterval: 5 * 60 * 1000, // Poll every 5 minutes (not 30s — this runs on every page)
    refetchOnWindowFocus: false, // Don't re-fetch just because the user switched tabs
  });
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background gradient-mesh noise">
        <div className="flex flex-col items-center gap-6 animate-fadeIn">
          {/* Premium loading spinner */}
          <div className="relative">
            <div className="spinner-gold" />
            <div className="absolute inset-0 spinner-gold opacity-30 scale-150" />
          </div>
          <p className="text-muted-foreground font-medium tracking-wide">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen onLogin={login} />;
  }

  // Users without a clientId must complete onboarding before accessing the app
  const hasClient = !!profile?.clientId;
  if (!hasClient) {
    return <OnboardingGate user={profile} onLogout={logout} />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Premium Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 sidebar transition-all duration-300 ease-out-expo flex flex-col',
          sidebarOpen ? 'w-72' : 'w-20'
        )}
      >
        {/* Logo Area */}
        <div className="h-18 flex items-center justify-between px-5 border-b border-white/5">
          <Link to="/" className="flex items-center gap-3 group">
            {/* Logo Mark */}
            <div className="relative flex-shrink-0">
              <CreditNovaLogoNoLetters variant="mark" size="md" />
              <div className="absolute -inset-1 rounded-xl bg-gold-500/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            {/* Logo Text */}
            {sidebarOpen && (
              <div className="animate-fadeIn">
                <h1 className="text-xl font-display font-semibold text-foreground tracking-tight">
                  CreditNova
                </h1>
                <p className="text-2xs text-muted-foreground uppercase tracking-widest">
                  AI Analysis
                </p>
              </div>
            )}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={cn(
              'p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all',
              !sidebarOpen && 'mx-auto'
            )}
          >
            {sidebarOpen ? (
              <ChevronLeft className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto scrollbar-thin">
          {sidebarOpen && (
            <p className="px-3 py-2 text-2xs uppercase tracking-widest text-muted-foreground/70 font-medium">
              Navigation
            </p>
          )}

          {/* Adaptive nav based on preferredMode */}
          {preferredMode === 'api' ? (
            <>
              {/* API Mode Navigation */}
              <NavLink to="/api-home" icon={<LayoutDashboard />} label="API Dashboard" collapsed={!sidebarOpen} />
              <NavLink to="/templates" icon={<FolderOpen />} label="Templates" collapsed={!sidebarOpen} />
              <NavLink to="/key-management" icon={<KeyRound />} label="API Keys" collapsed={!sidebarOpen} />
            </>
          ) : (
            <>
              {/* Direct Mode Navigation (default) */}
              <NavLink to={preferredMode === 'direct' ? '/dashboard' : '/'} icon={<LayoutDashboard />} label="Dashboard" collapsed={!sidebarOpen} />
              <NavLink to="/workflow" icon={<Workflow />} label="New Analysis" collapsed={!sidebarOpen} />
              <NavLink to="/analyses" icon={<FileText />} label="My Analyses" collapsed={!sidebarOpen} />
              <NavLink to="/templates" icon={<FolderOpen />} label="Templates" collapsed={!sidebarOpen} />
            </>
          )}

          {/* Admin Only Section */}
          {isAdmin && (
            <>
              {sidebarOpen && (
                <div className="pt-4 animate-fadeIn">
                  <p className="px-3 py-2 text-2xs uppercase tracking-widest text-gold-500/70 font-bold">
                    Administration
                  </p>
                </div>
              )}
              {/* Only show Manage APIs to admin if not in API mode (where it's already shown) */}
              {preferredMode !== 'api' && (
                <NavLink to="/key-management" icon={<KeyRound />} label="Manage APIs" collapsed={!sidebarOpen} />
              )}
              <NavLink to="/billing/billing-dashboard" icon={<DollarSign />} label="Billing" collapsed={!sidebarOpen} />
              <NavLink to="/clients/clients" icon={<Users />} label="Clients" collapsed={!sidebarOpen} badge={pendingJoinRequestCount} />
            </>
          )}

          {sidebarOpen && (
            <div className="pt-4">
              <p className="px-3 py-2 text-2xs uppercase tracking-widest text-muted-foreground/70 font-medium">
                Account
              </p>
            </div>
          )}
          <NavLink to="/settings" icon={<Settings />} label="Settings" collapsed={!sidebarOpen} />
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/5">
          {sidebarOpen && user && (
            <div className="mb-4 px-3">
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-gold flex items-center justify-center text-primary-950 font-semibold text-sm">
                  {user.name?.charAt(0) || user.username?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-foreground">
                    {user.name || user.username}
                  </p>
                  <p className="text-2xs text-muted-foreground truncate">
                    {user.username}
                  </p>
                </div>
              </div>
            </div>
          )}
          <button
            onClick={logout}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-all',
              !sidebarOpen && 'justify-center'
            )}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm font-medium">Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={cn(
          'min-h-screen transition-all duration-300 ease-out-expo',
          sidebarOpen ? 'ml-72' : 'ml-20'
        )}
      >
        <Outlet />
      </main>

      {/* Toast Notifications */}
      <Toaster />

      {/* Dev Tools */}
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </div>
  );
}

// Premium Login Screen Component
function LoginScreen({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="min-h-screen gradient-animated noise overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orbs */}
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold-500/5 rounded-full blur-3xl" />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--gold-500)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--gold-500)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />

        {/* Constellation dots */}
        <div className="pattern-constellation absolute inset-0 opacity-50" />
      </div>

      {/* Login Container */}
      <div className="flex flex-col items-center justify-center min-h-screen px-4 relative z-10">
        <div className="w-full max-w-md">
          {/* Logo & Branding */}
          <div className="text-center mb-10 animate-slideUp">
            {/* TAO Solutions Logo Mark - Large and prominent */}
            <div className="inline-flex mb-8">
              <div className="relative">
                <TaoLogo
                  variant="mark"
                  size="xl"
                  colorScheme="gradient"
                  className="h-24 drop-shadow-[0_0_30px_hsl(var(--gold-500)/0.3)]"
                />
                {/* Ambient glow behind logo */}
                <div className="absolute inset-0 blur-2xl opacity-40">
                  <TaoLogo variant="mark" size="xl" colorScheme="gold" className="h-24" />
                </div>
              </div>
            </div>

            <h1 className="text-5xl font-display font-bold text-foreground mb-3 tracking-tight">
              Credit<span className="gradient-text">Nova</span>
            </h1>
            <p className="text-muted-foreground text-lg font-light tracking-wide">
              AI-Powered Credit Analysis Platform
            </p>
          </div>

          {/* Login Card */}
          <div
            className="glass-elevated rounded-2xl p-8 animate-slideUp relative"
            style={{ animationDelay: '0.1s' }}
          >
            {/* Decorative top border */}
            <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-gold-500/50 to-transparent" />

            <h2 className="text-2xl font-display font-semibold text-foreground mb-2 text-center">
              Welcome back
            </h2>
            <p className="text-muted-foreground text-center mb-8">
              Sign in to continue to your dashboard
            </p>

            {/* Sign In Button */}
            <button
              onClick={onLogin}
              className="w-full py-4 px-6 btn-gold rounded-xl flex items-center justify-center gap-3 text-base"
            >
              <svg className="w-5 h-5" viewBox="0 0 21 21" fill="currentColor">
                <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z" />
              </svg>
              <span>Sign in with Microsoft</span>
            </button>

            {/* Divider */}
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px bg-border" />
              <span className="text-2xs text-muted-foreground uppercase tracking-widest">Secure</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Security Note */}
            <p className="text-center text-sm text-muted-foreground">
              Enterprise-grade security with Microsoft Entra ID
            </p>
          </div>

          {/* Footer with TAO Solutions branding */}
          <div
            className="flex flex-col items-center gap-4 mt-10 animate-slideUp"
            style={{ animationDelay: '0.2s' }}
          >
            <PoweredByTao />
            <p className="text-xs text-muted-foreground/50">
              Protected by enterprise-grade encryption
            </p>
          </div>
        </div>
      </div>

      {/* Toast notifications for login errors */}
      <Toaster />
    </div>
  );
}

// Navigation Link Component
interface NavLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  isAccent?: boolean;
  badge?: number;
}

function NavLink({ to, icon, label, collapsed, isAccent, badge }: NavLinkProps) {
  return (
    <Link
      to={to}
      className={cn(
        'sidebar-nav-item relative',
        collapsed && 'justify-center px-0',
        isAccent && !collapsed && 'bg-gold-500/10 text-gold-400 hover:bg-gold-500/15'
      )}
      activeProps={{
        className: 'active',
      }}
    >
      <span className={cn('w-5 h-5 flex-shrink-0 relative', isAccent && 'text-gold-400')}>
        {icon}
        {/* Badge dot when sidebar is collapsed */}
        {collapsed && !!badge && badge > 0 && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full border-2 border-[hsl(var(--sidebar-bg,222_47%_8%))]" />
        )}
      </span>
      {!collapsed && (
        <>
          <span className="font-medium flex-1">{label}</span>
          {!!badge && badge > 0 && (
            <span className="ml-auto text-[10px] bg-amber-500/20 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded-full font-bold min-w-[20px] text-center">
              {badge}
            </span>
          )}
        </>
      )}
    </Link>
  );
}
